import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Search, Scissors, User, Calendar, Heart, MapPin, Star, Bell, Clock, CreditCard, Phone, Mail, HelpCircle, BookOpen, Settings } from 'lucide-react';
import Footer from '../components/Footer';
import SEO, { breadcrumbSchema, faqSchema } from '../components/SEO';

// ── FAQ Accordion ─────────────────────────────────────────────
function FAQ({ q, a, open, onToggle }) {
  return (
    <div
      onClick={onToggle}
      style={{
        background: 'white', borderRadius: '14px', marginBottom: '10px',
        border: `1.5px solid ${open ? '#fca5a5' : '#f3f4f6'}`,
        overflow: 'hidden', cursor: 'pointer',
        boxShadow: open ? '0 4px 16px rgba(220,38,38,0.08)' : '0 1px 4px rgba(0,0,0,0.04)',
        transition: 'all 0.25s ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 18px' }}>
        <p style={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827', margin: 0, flex: 1, paddingRight: '12px' }}>{q}</p>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
          background: open ? '#dc2626' : '#f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 0.2s ease',
        }}>
          {open
            ? <ChevronUp size={14} color="white" />
            : <ChevronDown size={14} color="#6b7280" />
          }
        </div>
      </div>
      {open && (
        <div style={{
          padding: '0 18px 16px',
          color: '#4b5563', fontSize: '0.84rem', lineHeight: 1.7,
          borderTop: '1px solid #f9fafb',
          paddingTop: '12px',
          animation: 'fadeIn 0.2s ease',
        }}>
          {a}
        </div>
      )}
    </div>
  );
}

// ── Step Card ─────────────────────────────────────────────────
function Step({ num, title, desc, icon }) {
  return (
    <div style={{
      display: 'flex', gap: '14px', alignItems: 'flex-start',
      background: 'white', borderRadius: '16px', padding: '16px',
      border: '1px solid #f3f4f6', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      marginBottom: '10px',
    }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
        background: 'linear-gradient(135deg, #dc2626, #e11d48)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 800, fontSize: '0.85rem',
        boxShadow: '0 2px 8px rgba(220,38,38,0.3)',
      }}>
        {num}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <span style={{ color: '#dc2626' }}>{icon}</span>
          <p style={{ fontWeight: 700, fontSize: '0.88rem', color: '#111827', margin: 0 }}>{title}</p>
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.82rem', lineHeight: 1.6, margin: 0 }}>{desc}</p>
      </div>
    </div>
  );
}

// ── Feature Card ──────────────────────────────────────────────
function FeatureCard({ icon, title, desc, color = '#dc2626', bg = '#fff5f5' }) {
  return (
    <div style={{
      background: 'white', borderRadius: '16px', padding: '18px',
      border: '1px solid #f3f4f6', boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
    >
      <div style={{
        width: '44px', height: '44px', borderRadius: '12px',
        background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: '12px',
      }}>
        <span style={{ color }}>{icon}</span>
      </div>
      <p style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827', marginBottom: '6px' }}>{title}</p>
      <p style={{ color: '#6b7280', fontSize: '0.8rem', lineHeight: 1.6, margin: 0 }}>{desc}</p>
    </div>
  );
}

// ── Tab Button ────────────────────────────────────────────────
function TabBtn({ label, icon, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '6px',
      padding: '10px 18px', borderRadius: '12px', border: 'none', cursor: 'pointer',
      background: active ? 'linear-gradient(135deg,#dc2626,#e11d48)' : 'white',
      color: active ? 'white' : '#6b7280',
      fontWeight: 600, fontSize: '0.82rem',
      boxShadow: active ? '0 4px 12px rgba(220,38,38,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
    }}>
      {icon}{label}
    </button>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function HelpCenter() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('customer');
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const customerFaqs = [
    {
      q: 'BookYourSalons kya hai?',
      a: 'BookYourSalons India ka premium salon booking platform hai. Aap apne paas ke top salons dhundh sakte hain, service select karke instantly appointment book kar sakte hain — bina call kiye!'
    },
    {
      q: 'Booking kaise karein?',
      a: '1. Location set karein → 2. Nearby salons dekhein → 3. Salon select karein → 4. Service choose karein → 5. Date & slot select karein → 6. Confirm karein. Salon partner approve karega aur aapko notification milegi.'
    },
    {
      q: 'Payment kaise hoga?',
      a: 'Abhi sirf "Pay at Salon" option available hai. Booking confirm hone ke baad directly salon pe jakar payment karein. Online payment jald aayega.'
    },
    {
      q: 'Booking cancel ya change ho sakti hai?',
      a: 'Abhi cancellation ke liye salon ko directly contact karein. Future mein in-app cancellation add kiya jayega.'
    },
    {
      q: 'Favourite salon kaise save karein?',
      a: 'Salon card pe ❤️ heart icon tap karein. Aapke favourite salons automatically save ho jayenge profile mein.'
    },
    {
      q: 'Salon ka review kaise dein?',
      a: 'Service complete hone ke baad salon page pe jaayein → "Write Review" section mein rating aur comment dein.'
    },
    {
      q: 'Location kaise set karein?',
      a: 'Top navbar mein "Choose Location" button tapein → "Use My Current Location" select karein ya map pe pin drop karein ya search karein.'
    },
    {
      q: 'Male ya female salon alag kaise dekhein?',
      a: 'Customer home page pe "Male Salons" ya "Parlours" filter button tap karein. Default mein sab dikhte hain.'
    },
    {
      q: 'Booking status kahan dekhein?',
      a: 'Bottom navbar mein "Bookings" tap karein. Aapki saari upcoming aur past bookings wahan dikhti hain — status ke saath.'
    },
    {
      q: 'AI se booking kaise karein?',
      a: 'Screen ke bottom-right corner mein ✂️ button tapein. AI chatbot se Hinglish ya English mein baat karein — "kal haircut karna hai 5 baje" jaisi language mein.'
    },
  ];

  const salonFaqs = [
    {
      q: 'Salon partner kaise banein?',
      a: '1. Landing page pe "Be a Salon Partner" click karein → 2. Phone se OTP verify karein → 3. Unique ID aur password set karein → 4. Salon details fill karein → 5. Admin approval ka wait karein (24-48 hours).'
    },
    {
      q: 'Dashboard mein login kaise karein?',
      a: 'bookyoursalons.in/salon/login pe jaayein → Apna phone number YA Unique ID enter karein → Password enter karein → Login button tapein.'
    },
    {
      q: 'Booking accept/reject kaise karein?',
      a: 'Dashboard pe "New Booking Requests" section mein aata hai. 15 minute ke andar Accept ya Reject karein. Accept karne pe customer ko notification milti hai.'
    },
    {
      q: 'Salon open/close kaise karein?',
      a: 'Dashboard ke top navbar mein OPEN/CLOSED toggle hai. Tap karke status change karein. Closing time pe automatically CLOSED ho jaata hai.'
    },
    {
      q: 'Services kaise add/edit karein?',
      a: 'Dashboard → Edit Profile → Services section mein apni services, prices aur duration add/edit karein.'
    },
    {
      q: 'Subscription kab khatam hoga?',
      a: 'Dashboard pe countdown timer dikhega. 3 din pehle warning aayegi. Expire hone pe bookings band ho jaayengi — renew karo.'
    },
    {
      q: 'Photo kaise upload karein?',
      a: 'Dashboard top navbar → "Photo" button → Photo select karein → Upload. Salon card pe dikhegi.'
    },
    {
      q: 'Operating hours kaise set karein?',
      a: 'Dashboard → "Hours" button → Opening time aur Closing time set karein → Save. Slots usi hisab se generate honge.'
    },
    {
      q: 'Push notification kab aayega?',
      a: 'Jab koi customer booking kare tab dashboard mobile pe open ho aur notifications allowed hon to push notification + sound aata hai.'
    },
    {
      q: 'Gallery mein photos kaise add karein?',
      a: 'Dashboard → "Gallery" button → Multiple photos select kar sakte hain → Upload. Customers salon detail page pe dekhenge.'
    },
  ];

  const allFaqs = activeTab === 'customer' ? customerFaqs : salonFaqs;
  const filteredFaqs = searchQuery
    ? allFaqs.filter(f =>
        f.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        f.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allFaqs;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#fff5f5,#fff,#fdf4ff)' }}>
      <SEO
        title="Help Center — Booking & Salon Partner Guide"
        description="How to book a salon on BookYourSalons, register your salon or parlour, manage bookings, and use dashboard features. FAQs for customers and salon partners."
        path="/help"
        schema={faqSchema([
          { q: 'BookYourSalons kya hai?', a: "BookYourSalons India ka premium salon booking platform hai. Aap apne paas ke top salons dhundh sakte hain aur instantly appointment book kar sakte hain." },
          { q: 'Booking kaise karein?', a: 'Location set karein, nearby salons dekhein, salon aur service select karein, date aur slot choose karein, phir confirm karein.' },
          { q: 'Payment kaise hoga?', a: 'Abhi sirf Pay at Salon option available hai. Booking confirm hone ke baad directly salon pe jakar payment karein.' },
          { q: 'Salon partner kaise banein?', a: 'Homepage pe Be a Salon Partner click karein, phone OTP verify karein, unique ID aur password set karein, salon details fill karein, phir admin approval ka wait karein.' },
          { q: 'Dashboard mein login kaise karein?', a: 'bookyoursalons.in/salon/login pe jaayein, apna phone number ya Unique ID aur password enter karke login karein.' },
        ])}
      />

      {/* Nav */}
      <nav style={{
        background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '12px 20px',
        position: 'sticky', top: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <button onClick={() => navigate('/')} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: '6px',
          color: '#374151', fontWeight: 600, fontSize: '0.85rem',
        }}>
          <ArrowLeft size={18} /> Home
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ background: 'linear-gradient(135deg,#dc2626,#e11d48)', padding: '6px', borderRadius: '8px' }}>
            <HelpCircle size={14} color="white" />
          </div>
          <span style={{ fontWeight: 800, fontFamily: "'Syne',sans-serif", fontSize: '0.95rem', color: '#111827' }}>
            Help Center
          </span>
        </div>
      </nav>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '32px 16px' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', margin: '0 auto 16px',
            background: 'linear-gradient(135deg,#dc2626,#e11d48)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(220,38,38,0.3)',
          }}>
            <BookOpen size={28} color="white" />
          </div>
          <h1 style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: "'Syne',sans-serif", color: '#111827', marginBottom: '8px' }}>
            Help Center
          </h1>
          <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
            BookYourSalons ke baare mein sab kuch jaanein
          </p>

          {/* Search */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px', marginTop: '20px',
            background: 'white', borderRadius: '16px', padding: '12px 18px',
            border: '1.5px solid #e5e7eb', boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            maxWidth: '440px', margin: '20px auto 0',
          }}>
            <Search size={18} color="#9ca3af" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Help dhundho... (e.g. booking, login, payment)"
              style={{
                border: 'none', outline: 'none', flex: 1,
                fontSize: '0.88rem', color: '#111827', background: 'transparent',
              }}
            />
          </div>
        </div>

        {/* ── CUSTOMER GUIDE ─────────────────────────────────── */}
        {!searchQuery && (
          <>
            {/* Tab Selector */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '28px', overflowX: 'auto', paddingBottom: '4px' }}>
              <TabBtn label="Customer Guide" icon={<User size={14} />} active={activeTab === 'customer'} onClick={() => setActiveTab('customer')} />
              <TabBtn label="Salon Partner Guide" icon={<Scissors size={14} />} active={activeTab === 'salon'} onClick={() => setActiveTab('salon')} />
              <TabBtn label="Features" icon={<Star size={14} />} active={activeTab === 'features'} onClick={() => setActiveTab('features')} />
            </div>

            {/* Customer: How to Book */}
            {activeTab === 'customer' && (
              <>
                <div style={{ background: 'linear-gradient(135deg,#dc2626,#e11d48)', borderRadius: '20px', padding: '20px', marginBottom: '24px', color: 'white' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: "'Syne',sans-serif", marginBottom: '4px' }}>
                    📱 Customer — Booking Guide
                  </h2>
                  <p style={{ opacity: 0.85, fontSize: '0.82rem' }}>5 simple steps mein salon book karein</p>
                </div>

                <Step num="1" title="Account banao ya login karo" icon={<Phone size={14} />}
                  desc="Phone number daalo → OTP verify karo. Pehli baar Google se bhi sign in kar sakte hain. No password needed!" />
                <Step num="2" title="Location set karo" icon={<MapPin size={14} />}
                  desc="Top navbar mein 'Choose Location' tapein → 'Use My Current Location' select karein ya city/area search karein. 5km radius mein salons dikhenge." />
                <Step num="3" title="Salon dhundho aur select karo" icon={<Search size={14} />}
                  desc="Nearby salons list mein rating, distance aur services dekhein. Male Salons / Parlours filter use kar sakte hain. ❤️ tapke favourite bhi save karein." />
                <Step num="4" title="Service aur slot select karo" icon={<Calendar size={14} />}
                  desc="Salon card pe tap karein → Service choose karein → Date select karein → Available time slot choose karein. Real-time slot availability dikhti hai." />
                <Step num="5" title="Booking confirm karo" icon={<Bell size={14} />}
                  desc="'Book Now' tap karein → Salon partner ko request jayegi → 15 minutes mein accept/reject milega → Aapko notification aayega. Pay at salon!" />
              </>
            )}

            {/* Salon: How to Register */}
            {activeTab === 'salon' && (
              <>
                <div style={{ background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', borderRadius: '20px', padding: '20px', marginBottom: '24px', color: 'white' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: "'Syne',sans-serif", marginBottom: '4px' }}>
                    ✂️ Salon Partner — Registration Guide
                  </h2>
                  <p style={{ opacity: 0.85, fontSize: '0.82rem' }}>Apna salon platform pe list karein</p>
                </div>

                <Step num="1" title="Registration shuru karo" icon={<Scissors size={14} />}
                  desc="Homepage pe 'Be a Salon Partner' button click karein ya bookyoursalons.in pe jaayein. Phone number se OTP verify karein." />
                <Step num="2" title="Unique ID aur Password set karo" icon={<Settings size={14} />}
                  desc="Apna unique salon ID choose karein (e.g. 'gormalsalon2024') aur strong password set karein. Yahi ID aur password se future mein login hoga." />
                <Step num="3" title="Salon details fill karo" icon={<BookOpen size={14} />}
                  desc="Salon naam, address, area, business type (salon/parlour/unisex), staff count, opening/closing hours, aur services add karein with prices." />
                <Step num="4" title="Admin approval ka wait karo" icon={<Clock size={14} />}
                  desc="Form submit karne ke baad admin review karega (24-48 hours). Approval milne pe dashboard access milega." />
                <Step num="5" title="Dashboard use karo" icon={<Bell size={14} />}
                  desc="bookyoursalons.in/salon/login pe jaayein → Phone ya Unique ID + Password se login karein → Dashboard open hoga. Bookings manage karein!" />

                <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: '16px', padding: '16px', marginTop: '16px' }}>
                  <p style={{ fontWeight: 700, color: '#15803d', fontSize: '0.88rem', marginBottom: '6px' }}>💡 Dashboard Features:</p>
                  <ul style={{ color: '#166534', fontSize: '0.82rem', lineHeight: 1.8, margin: 0, paddingLeft: '18px' }}>
                    <li>Booking requests accept/reject karein (15 min window)</li>
                    <li>Open/Closed status toggle — auto-close by time</li>
                    <li>Services, prices, hours manage karein</li>
                    <li>Salon photo aur gallery upload karein</li>
                    <li>Bank details add karein (future payments)</li>
                    <li>Push notifications — naya booking aate hi sound aayega</li>
                    <li>Live clock + countdown to closing time</li>
                    <li>Subscription status aur renewal</li>
                  </ul>
                </div>
              </>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <>
                <div style={{ background: 'linear-gradient(135deg,#0f172a,#1e293b)', borderRadius: '20px', padding: '20px', marginBottom: '24px', color: 'white' }}>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: "'Syne',sans-serif", marginBottom: '4px' }}>
                    ✨ Platform Features
                  </h2>
                  <p style={{ opacity: 0.7, fontSize: '0.82rem' }}>Sab kuch jo aapko milta hai</p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                  <FeatureCard icon={<MapPin size={20} />} title="Nearby Salons" bg="#fff5f5" color="#dc2626"
                    desc="GPS se 5km radius ke andar ke sab salons automatically dikhein" />
                  <FeatureCard icon={<Search size={20} />} title="Smart Search" bg="#eff6ff" color="#2563eb"
                    desc="Salon naam, service, ya area se search karein — instant results" />
                  <FeatureCard icon={<Heart size={20} />} title="Favourite Salons" bg="#fdf4ff" color="#9333ea"
                    desc="❤️ tap karke apne pasandida salons save karein" />
                  <FeatureCard icon={<Calendar size={20} />} title="Real-time Slots" bg="#f0fdf4" color="#16a34a"
                    desc="Live slot availability — book karo, double booking nahi hogi" />
                  <FeatureCard icon={<Bell size={20} />} title="Push Notifications" bg="#fff7ed" color="#ea580c"
                    desc="Booking confirm/reject hone pe instant notification" />
                  <FeatureCard icon={<Star size={20} />} title="Ratings & Reviews" bg="#fefce8" color="#ca8a04"
                    desc="Real customer reviews se sahi salon choose karein" />
                  <FeatureCard icon={<Phone size={20} />} title="AI Booking Bot" bg="#fff5f5" color="#dc2626"
                    desc="Hinglish mein bolke book karein — 'kal haircut 5 baje'" />
                  <FeatureCard icon={<CreditCard size={20} />} title="Pay at Salon" bg="#f0fdf4" color="#16a34a"
                    desc="Koi advance payment nahi — salon pe jaake pay karein" />
                  <FeatureCard icon={<User size={20} />} title="OTP Login" bg="#eff6ff" color="#2563eb"
                    desc="Sirf phone number se login — no password needed for customers" />
                  <FeatureCard icon={<Scissors size={20} />} title="Male + Female" bg="#fdf4ff" color="#9333ea"
                    desc="Male salons aur female parlours alag filter se dekhein" />
                  <FeatureCard icon={<Clock size={20} />} title="Auto Close" bg="#fff7ed" color="#ea580c"
                    desc="Salon closing time pe automatically CLOSED ho jaata hai" />
                  <FeatureCard icon={<Settings size={20} />} title="Salon Dashboard" bg="#f9fafb" color="#374151"
                    desc="Partner ke liye full management dashboard — bookings, hours, photos" />
                </div>
              </>
            )}
          </>
        )}

        {/* ── FAQ SECTION ────────────────────────────────────── */}
        <div style={{ marginTop: searchQuery ? 0 : '36px' }}>
          {!searchQuery && (
            <>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto' }}>
                <TabBtn label="Customer FAQs" icon={<User size={14} />} active={activeTab !== 'salon' && activeTab !== 'features'} onClick={() => setActiveTab('customer')} />
                <TabBtn label="Salon Partner FAQs" icon={<Scissors size={14} />} active={activeTab === 'salon'} onClick={() => setActiveTab('salon')} />
              </div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: "'Syne',sans-serif", color: '#111827', marginBottom: '16px' }}>
                ❓ Aksar Puchhe Jaane Wale Sawaal
              </h2>
            </>
          )}

          {searchQuery && (
            <p style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '16px' }}>
              "{searchQuery}" ke liye {filteredFaqs.length} result{filteredFaqs.length !== 1 ? 's' : ''} mile
            </p>
          )}

          {filteredFaqs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
              <Search size={32} style={{ margin: '0 auto 12px', display: 'block' }} />
              <p>Koi result nahi mila. Kuch aur try karein.</p>
            </div>
          ) : (
            filteredFaqs.map((faq, i) => (
              <FAQ key={i} q={faq.q} a={faq.a}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))
          )}
        </div>

        {/* ── CONTACT SECTION ────────────────────────────────── */}
        <div style={{
          background: 'linear-gradient(135deg,#111827,#1f2937)',
          borderRadius: '20px', padding: '28px', marginTop: '36px', textAlign: 'center',
        }}>
          <h3 style={{ color: 'white', fontWeight: 800, fontFamily: "'Syne',sans-serif", marginBottom: '8px' }}>
            Aur help chahiye? 🤝
          </h3>
          <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '20px' }}>
            Hamare team se contact karein — hum help karne ke liye hain
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:bookyoursalons@gmail.com" style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'linear-gradient(135deg,#dc2626,#e11d48)',
              color: 'white', padding: '10px 20px', borderRadius: '12px',
              textDecoration: 'none', fontWeight: 600, fontSize: '0.85rem',
              boxShadow: '0 4px 12px rgba(220,38,38,0.3)',
            }}>
              <Mail size={16} /> bookyoursalons@gmail.com
            </a>
          </div>
          <p style={{ color: '#4b5563', fontSize: '0.75rem', marginTop: '16px' }}>
            Response time: 24-48 hours (Mon-Sat)
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
